import type { datadogRum as ddRum } from '@datadog/browser-rum';
import type { datadogLogs as ddLogs, Logger as ddLogger } from '@datadog/browser-logs';
import type { Logger } from '../types';

/* eslint-disable no-var, vars-on-top */
declare global {
  var datadogRum: typeof ddRum;
  var datadogLogs: typeof ddLogs;
}

interface CreateLogger {
  (args: { name: string; team: string; feature: string }): Logger;
}

export const createLogger: CreateLogger = ({ name, team, feature }) => {
  const rum = globalThis?.datadogRum; // Defined in datadog-init-webpack-entry
  const logs = globalThis?.datadogLogs; // Defined in datadog-browser-logs-init-webpack-entry
  const sessionId = rum?.getInternalContext()?.session_id;

  const logger =
    logs?.createLogger(name, {
      context: {
        team,
        feature,
        sessionId,
      },
    }) ?? console;

  const error: Logger['error'] = (msg, ctx) => logger?.error(msg, ctx);
  const warn: Logger['warn'] = (msg, ctx) => logger?.warn(msg, ctx);
  const info: Logger['info'] = (msg, ctx) => logger?.info(msg, ctx);
  const log: Logger['log'] = (msg, ctx) => logger?.log(msg, ctx);
  const debug: Logger['debug'] = (msg, ctx) => logger?.debug(msg, ctx);
  const addContext: Logger['addContext'] = (key, ctx) => {
    (logger as ddLogger)?.addContext(key, ctx);
    rum?.addRumGlobalContext(key, ctx);
  };
  const addTiming: Logger['addTiming'] = (key) => rum?.addTiming(key);
  const addError: Logger['addError'] = (err, ctx) => rum?.addError(err, ctx);
  const addAction: Logger['addAction'] = (key, ctx) => rum?.addAction(key, ctx);

  return {
    error,
    warn,
    info,
    log,
    debug,
    addContext,
    addTiming,
    addError,
    addAction,
  };
};
